import * as React from 'react';

import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import { FieldValues, UseFormReturn } from 'react-hook-form';

import { DataTable } from '@/components/data-table/data-table';

import { createOnboardingConflictColumns } from './onboarding-conflicts-columns';
import { ONBOARDING_CONFLICT_CATEGORY_ATTRIBUTES, OnboardingConflictRow } from './types';

interface OnboardingConflictsComparisonTableProps {
  /** Flat table data rows (attributes + integrations) */
  tableData: OnboardingConflictRow[];
  /** Grouped data for sub-headers */
  groupedData?: Array<{ category: string; items: OnboardingConflictRow[] }>;
  /** Person labels for column headers (e.g. "Person 1", "Person 2") */
  personLabels: string[];
  /** React Hook Form instance */
  form: UseFormReturn<FieldValues>;
  /** Translation function */
  t: TFunction;
  /** Pre-translated label for the "Resulting Entity" column header */
  resultingEntityLabel: string;
}

export function OnboardingConflictsComparisonTable({
  tableData,
  groupedData,
  personLabels,
  form,
  t,
  resultingEntityLabel,
}: OnboardingConflictsComparisonTableProps) {
  const columns = React.useMemo(
    () =>
      createOnboardingConflictColumns({
        personLabels,
        form,
        t,
        resultingEntityLabel,
      }),
    [personLabels, form, t, resultingEntityLabel]
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.fieldKey,
  });

  const renderGroupHeader = React.useCallback(
    (category: string) => {
      const labelKey =
        category === ONBOARDING_CONFLICT_CATEGORY_ATTRIBUTES
          ? 'authenticated.onboarding.common:duplicates.attributesGroup'
          : 'authenticated.onboarding.common:duplicates.integrationsGroup';

      return (
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t(labelKey)}
        </div>
      );
    },
    [t]
  );

  return (
    <DataTable
      table={table}
      stickyHeader={true}
      groupedData={groupedData}
      renderGroupHeader={renderGroupHeader}
      variant="conflictResolution"
    />
  );
}
